import { useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Layout, Menu } from 'antd';
import { useTranslation } from 'react-i18next';
import {
	AppstoreOutlined,
	BarcodeOutlined,
	DotChartOutlined,
	FileZipOutlined,
	ShopFilled,
	SolutionOutlined,
	WalletOutlined,
} from '@ant-design/icons';
import { ReactComponent as UsersOutlined } from 'assets/img/icons/UsersOutlinedSmall.svg';
import { useHistory, useLocation } from 'react-router-dom';

import styles from './Sider.module.less';
import { useSelector } from 'react-redux';
import { projectSelector } from 'redux/selectors/projectSelector';
import { userSelector } from '../../../redux/selectors/userSelector';
import { CollaboratorPermission } from '../../../api';
const { Sider } = Layout;

const getSelectedKeys = (pathname: string) => {
	if (pathname.includes('/collaborators')) return ['collaborators'];
	if (pathname.includes('/store-details')) return ['storeDetails'];
	if (pathname.includes('/builds')) return ['builds'];
	if (pathname.includes('/game-services')) return ['gameServices'];
	if (pathname.includes('/finance')) return ['finance'];
	if (pathname.includes('/players')) return ['players'];
	if (pathname.includes('/analytics')) return ['analytics'];
	if (pathname.includes('/redemption-codes')) return ['redemptionCodes'];
	return ['projectMain'];
};

export default function ProjectSider() {
	const { isAuthenticated } = useAuth0();
	const { t } = useTranslation();
	const { pathname } = useLocation();
	const history = useHistory();
	const selectedKeys = useMemo(() => getSelectedKeys(pathname), [pathname]);
	const selector = useSelector(projectSelector);
	const userData = useSelector(userSelector);

	const projectPermissions = userData?.projectPermissions?.find(
		(x) => x.projectId === selector?.id,
	)?.permissions;

	const hasAccessTo = (permission: CollaboratorPermission) => {
		return projectPermissions?.includes(permission);
	};

	return isAuthenticated ? (
		<Sider className={styles.sidebar} width="295px">
			<div className={styles.sidebarMenu}>
				<Menu selectedKeys={selectedKeys} theme="light" mode="inline">
					<Menu.ItemGroup>
						<Menu.Item
							key={'all-projects'}
							onClick={() => history.push('/projects')}
						>
							{t('sider.AllProjects')}
						</Menu.Item>
					</Menu.ItemGroup>
					<Menu.Divider />
					<Menu.ItemGroup key="projectItems">
						<Menu.Item
							key="projectMain"
							className={styles.sidebarProjectName}
							onClick={() => history.push(`/project/${selector?.id}`)}
						>
							{!selector?.loading ? selector?.name : ''}
						</Menu.Item>

						{hasAccessTo(CollaboratorPermission.Collaborators) && (
							<Menu.Item
								data-testid={'collaborators'}
								key="collaborators"
								onClick={() =>
									history.push(`/project/${selector?.id}/collaborators`)
								}
							>
								<UsersOutlined /> {t('sider.Collaborators')}
							</Menu.Item>
						)}
						{hasAccessTo(CollaboratorPermission.StoreDetails) && (
							<Menu.Item
								data-testid={'storeDetails'}
								key="storeDetails"
								onClick={() =>
									history.push(`/project/${selector?.id}/store-details?tab=1`)
								}
							>
								<ShopFilled /> {t('sider.StoreDetails')}
							</Menu.Item>
						)}
						{hasAccessTo(CollaboratorPermission.Builds) && (
							<Menu.Item
								data-testid={'builds'}
								key="builds"
								onClick={() => history.push(`/project/${selector?.id}/builds`)}
							>
								<FileZipOutlined /> {t('sider.Builds')}
							</Menu.Item>
						)}
						{hasAccessTo(CollaboratorPermission.GameServices) && (
							<Menu.Item
								data-testid={'gameServices'}
								key="gameServices"
								onClick={() =>
									history.push(`/project/${selector?.id}/game-services`)
								}
							>
								<AppstoreOutlined /> {t('sider.GameServices')}
							</Menu.Item>
						)}
						{hasAccessTo(CollaboratorPermission.Finances) && (
							<Menu.Item
								data-testid={'finance'}
								key="finance"
								onClick={() => history.push(`/project/${selector?.id}/finance`)}
							>
								<WalletOutlined /> {t('sider.Finance')}
							</Menu.Item>
						)}
						{hasAccessTo(CollaboratorPermission.Players) && (
							<Menu.Item
								data-testid={'players'}
								key="players"
								onClick={() => history.push(`/project/${selector?.id}/players`)}
							>
								<SolutionOutlined /> {t('sider.Players')}
							</Menu.Item>
						)}
						{hasAccessTo(CollaboratorPermission.Analytics) && (
							<Menu.Item
								data-testid={'analytics'}
								key="analytics"
								onClick={() =>
									history.push(`/project/${selector?.id}/analytics`)
								}
							>
								<DotChartOutlined /> {t('sider.Analytics')}
							</Menu.Item>
						)}
						{hasAccessTo(CollaboratorPermission.RedemptionCodes) && (
							<Menu.Item
								data-testid={'redemptionCodes'}
								key="redemptionCodes"
								onClick={() =>
									history.push(`/project/${selector?.id}/redemption-codes`)
								}
							>
								<BarcodeOutlined /> {t('sider.RedemptionCodes')}
							</Menu.Item>
						)}
					</Menu.ItemGroup>
				</Menu>
			</div>
		</Sider>
	) : null;
}
